import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Payment,
  CreditCard,
  AccountBalance,
  PhoneAndroid,
  Save,
} from '@mui/icons-material';

interface PaymentSettingsProps {
  salonId: string;
}

const PaymentSettings: React.FC<PaymentSettingsProps> = ({ salonId }) => {
  const [paymentMethods, setPaymentMethods] = useState({
    cash: { enabled: true, name: 'Cash Payment' },
    card: { enabled: true, name: 'Card Payment' },
    upi: { enabled: true, name: 'UPI Payment' },
    netbanking: { enabled: false, name: 'Net Banking' },
    wallet: { enabled: true, name: 'Digital Wallet' },
  });

  const [razorpaySettings, setRazorpaySettings] = useState({
    enabled: true,
    keyId: '',
    keySecret: '',
    webhookSecret: '',
  });

  const [stripeSettings, setStripeSettings] = useState({
    enabled: false,
    publishableKey: '',
    secretKey: '',
    webhookSecret: '',
  });

  const [paymentPolicies, setPaymentPolicies] = useState({
    requireAdvancePayment: true,
    advancePercentage: 25,
    allowPartialPayments: true,
    lateFeePercentage: 5,
    gracePeriodDays: 3,
    autoRefund: false,
    refundProcessingDays: 7,
  });

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handlePaymentMethodToggle = (method: string) => {
    setPaymentMethods(prev => ({
      ...prev,
      [method]: {
        ...prev[method as keyof typeof prev],
        enabled: !prev[method as keyof typeof prev].enabled,
      },
    }));
  };

  const handleRazorpayChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setRazorpaySettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleStripeChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setStripeSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePolicyChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setPaymentPolicies(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }, 1000);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#2D3748', mb: 3 }}>
        Payment Settings
      </Typography>

      {saved && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          Payment settings saved successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Payment Methods */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Payment sx={{ color: '#E8B4B8', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Payment Methods
                </Typography>
              </Box>

              <List>
                {Object.entries(paymentMethods).map(([key, method]) => (
                  <ListItem key={key}>
                    <ListItemText primary={method.name} />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={method.enabled}
                        onChange={() => handlePaymentMethodToggle(key)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Policies */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Payment Policies
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={paymentPolicies.requireAdvancePayment}
                        onChange={handlePolicyChange('requireAdvancePayment')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Require Advance Payment"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Advance Payment Percentage (%)"
                    type="number"
                    value={paymentPolicies.advancePercentage}
                    onChange={handlePolicyChange('advancePercentage')}
                    disabled={!paymentPolicies.requireAdvancePayment}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={paymentPolicies.allowPartialPayments}
                        onChange={handlePolicyChange('allowPartialPayments')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Allow Partial Payments"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Late Fee (%)"
                    type="number"
                    value={paymentPolicies.lateFeePercentage}
                    onChange={handlePolicyChange('lateFeePercentage')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Grace Period (days)"
                    type="number"
                    value={paymentPolicies.gracePeriodDays}
                    onChange={handlePolicyChange('gracePeriodDays')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={paymentPolicies.autoRefund}
                        onChange={handlePolicyChange('autoRefund')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Auto Process Refunds"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Refund Processing Days"
                    type="number"
                    value={paymentPolicies.refundProcessingDays}
                    onChange={handlePolicyChange('refundProcessingDays')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Razorpay Configuration */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CreditCard sx={{ color: '#E8B4B8', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Razorpay Configuration
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={razorpaySettings.enabled}
                        onChange={handleRazorpayChange('enabled')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Enable Razorpay"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Key ID"
                    value={razorpaySettings.keyId}
                    onChange={handleRazorpayChange('keyId')}
                    disabled={!razorpaySettings.enabled}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Key Secret"
                    type="password"
                    value={razorpaySettings.keySecret}
                    onChange={handleRazorpayChange('keySecret')}
                    disabled={!razorpaySettings.enabled}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Webhook Secret"
                    type="password"
                    value={razorpaySettings.webhookSecret}
                    onChange={handleRazorpayChange('webhookSecret')}
                    disabled={!razorpaySettings.enabled}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Stripe Configuration */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AccountBalance sx={{ color: '#E8B4B8', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Stripe Configuration
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={stripeSettings.enabled}
                        onChange={handleStripeChange('enabled')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Enable Stripe"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Publishable Key"
                    value={stripeSettings.publishableKey}
                    onChange={handleStripeChange('publishableKey')}
                    disabled={!stripeSettings.enabled}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Secret Key"
                    type="password"
                    value={stripeSettings.secretKey}
                    onChange={handleStripeChange('secretKey')}
                    disabled={!stripeSettings.enabled}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Webhook Secret"
                    type="password"
                    value={stripeSettings.webhookSecret}
                    onChange={handleStripeChange('webhookSecret')}
                    disabled={!stripeSettings.enabled}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                bgcolor: '#E8B4B8',
                color: 'white',
                px: 4,
                py: 1.5,
                borderRadius: 3,
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#D69BA0',
                },
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PaymentSettings;